import {
  DocumentSectionSpec,
  DocumentType,
  getDefaultSectionTemplateType,
  getDocumentTypeRules,
  SectionOptionSchema,
} from '@/entities/templates';

import { AI_OPTION_SLOT_NAME } from '../config';

import { isAiSlotName } from './aiSlots';

const SINGLE_SLOT_TOKEN_RE = /^\{\{\s*([\p{L}\p{N}_]+)\s*\}\}$/u;

export const isAiOption = (option: SectionOptionSchema): boolean => {
  const match = option.text.trim().match(SINGLE_SLOT_TOKEN_RE);

  if (!match || !isAiSlotName(match[1])) {
    return false;
  }

  return Object.keys(option.slots ?? {}).every(isAiSlotName);
};

export const createAiOption = (): SectionOptionSchema => ({
  text: `{{${AI_OPTION_SLOT_NAME}}}`,
  slots: { [AI_OPTION_SLOT_NAME]: [] },
});

export const getAiOptionIndex = (section: DocumentSectionSpec): number =>
  (section.options ?? []).findIndex(isAiOption);

export const getLastStrictOptionIndex = (
  section?: DocumentSectionSpec,
): number =>
  (section?.options ?? []).reduce(
    (lastIndex, option, optionIndex) =>
      isAiOption(option) ? lastIndex : optionIndex,
    -1,
  );

// Scribe allows AI free text only in flexible sections, so the AI option and
// the flexible type must always travel together.
export const getSectionTemplateType = (
  documentType: DocumentType | string | null | undefined,
  options: SectionOptionSchema[] | undefined,
): NonNullable<DocumentSectionSpec['template_type']> => {
  if (!getDocumentTypeRules(documentType).allowOptions) {
    return getDefaultSectionTemplateType(documentType);
  }

  const section = { code: '', title: '', options };

  return getLastStrictOptionIndex(section) >= 0 && getAiOptionIndex(section) < 0
    ? 'strict'
    : 'flexible';
};

// Restores the AI option that a flexible section with options implies, so the
// editor shows the permission scribe already grants.
export const addLegacyAiOption = (
  section: DocumentSectionSpec,
): DocumentSectionSpec =>
  section.template_type === 'flexible' &&
  getLastStrictOptionIndex(section) >= 0 &&
  getAiOptionIndex(section) < 0
    ? { ...section, options: [...(section.options ?? []), createAiOption()] }
    : section;

export const findLastSectionWithStrictOptions = (
  sections: DocumentSectionSpec[],
): number =>
  sections.reduce(
    (lastIndex, section, sectionIndex) =>
      getLastStrictOptionIndex(section) >= 0 ? sectionIndex : lastIndex,
    -1,
  );

type GetStrictInsertIndexArgs = {
  options: SectionOptionSchema[];
  insertAt: number;
};

// The AI option always renders after the strict options, so strict options
// are inserted before it.
export const getStrictInsertIndex = ({
  options,
  insertAt,
}: GetStrictInsertIndexArgs): number => {
  const aiOptionIndex = options.findIndex(isAiOption);

  return aiOptionIndex >= 0 ? Math.min(insertAt, aiOptionIndex) : insertAt;
};
